import React, { useState } from "react";
import { User, Home, FileText, ChevronDown, ChevronUp } from "lucide-react";
import { signUpPatient } from "../APIs/patientService";
import InsuranceDropdown from "../InsuranceDropdownComponent/InsuranceDropdown";
import AddressAutocomplete from "../Provider/AddressAutoComplete";

const PatientCreateAccount = () => {
  const [sections, setSections] = useState({
    personal: false,
    address: false,
    document: false,
    insurance: false,
  });

  const [patientForm, setPatientForm] = useState({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    email: "",
    phoneNumber: "",
    addressLine: "",
    city: "",
    state: "",
    country: "",
    documentType: "",
    documentCategory: "",
    documentFile: null,
    planId: "",
    memberId: "",
    groupId: "",
    coverageStart: "",
    coverageEnd: "",
    insuranceCard: null,
  });

  const [errors, setErrors] = useState({});

  const toggleSection = (section) => {
    setSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const sectionStyle = (isOpen) =>
    `mb-6 border rounded-lg shadow-lg bg-white transition-all duration-300 ease-in-out ${
      isOpen ? "border-blue-500 bg-blue-50" : ""
    }`;

  // ---- Phone formatting to match placeholder (###) ###-#### ----
  const formatPhone = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 10);
    const p1 = digits.slice(0, 3);
    const p2 = digits.slice(3, 6);
    const p3 = digits.slice(6, 10);
    if (digits.length <= 3) return p1;
    if (digits.length <= 6) return `(${p1}) ${p2}`;
    return `(${p1}) ${p2}-${p3}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "phoneNumber") {
      setPatientForm((prev) => ({ ...prev, phoneNumber: formatPhone(value) }));
      return;
    }

    setPatientForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setPatientForm((prev) => ({ ...prev, documentFile: e.target.files[0] }));
  };

  // Truncating street address
  const truncateStreet = (formatted) => {
    if (!formatted) return "";
    const i = formatted.indexOf(",");
    return (i === -1 ? formatted : formatted.slice(0, i)).trim();
  };

  const validate = () => {
    const newErrors = {};

    // Since only one address field is visible, require just addressLine here.
    const requiredFields = [
      "firstName",
      "lastName",
      "dateOfBirth",
      "email",
      "phoneNumber",
      "addressLine",
    ];

    requiredFields.forEach((field) => {
      if (!patientForm[field]) {
        newErrors[field] = "This field is required";
      }
    });

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (patientForm.email && !emailRegex.test(patientForm.email)) {
      newErrors.email = "Enter a valid email";
    }

    // Accept formatted (###) ###-####; guide users to match placeholder
    const phonePretty = /^\(\d{3}\)\s\d{3}-\d{4}$/;
    if (patientForm.phoneNumber && !phonePretty.test(patientForm.phoneNumber)) {
      newErrors.phoneNumber = "Use format (555) 555-1234";
    }

    // If user is providing insurance, require basics
    const insuranceTouched = !!(
      patientForm.planId ||
      patientForm.memberId ||
      patientForm.groupId ||
      patientForm.coverageStart ||
      patientForm.coverageEnd ||
      patientForm.insuranceCard
    );
    if (insuranceTouched) {
      if (!patientForm.planId) newErrors.planId = "Select an insurance plan";
      if (!patientForm.memberId) newErrors.memberId = "Member ID is required";
      if (!patientForm.coverageStart)
        newErrors.coverageStart = "Coverage start is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      // Normalize phone to digits for BE, keep the rest as-is (including hidden city/state/country)
      const payload = {
        ...patientForm,
        phoneNumber: patientForm.phoneNumber.replace(/\D/g, ""), // "5555551234"
      };
      await signUpPatient(payload);
      alert("Account created successfully!");
    } catch (error) {
      console.error("Signup error:", error);
      alert("There was an error creating the account.");
    }
  };

  const renderError = (field) =>
    errors[field] && (
      <div className="text-red-600 text-sm">{errors[field]}</div>
    );

  return (
    <div className="max-w-2xl mx-auto p-4">
      <h1 className="text-3xl font-bold mb-6 text-center">Create Account</h1>

      {/* Personal Information */}
      <div className={sectionStyle(sections.personal)}>
        <button
          onClick={() => toggleSection("personal")}
          className="w-full flex items-center justify-between p-4 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded-t-lg"
        >
          <div className="flex items-center space-x-2">
            <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
              1
            </span>
            <User className="w-5 h-5" />
            <span className="font-medium">Personal Information</span>
          </div>
          {sections.personal ? <ChevronUp /> : <ChevronDown />}
        </button>
        <div
          className={`transition-all duration-300 ease-in-out overflow-hidden ${
            sections.personal ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="p-4 space-y-3">
            <div>
              <input
                name="firstName"
                value={patientForm.firstName}
                onChange={handleChange}
                className="w-full border p-2"
                placeholder="First Name"
                required
                autoComplete="given-name"
              />
              {renderError("firstName")}
            </div>
            <div>
              <input
                name="lastName"
                value={patientForm.lastName}
                onChange={handleChange}
                className="w-full border p-2"
                placeholder="Last Name"
                required
                autoComplete="family-name"
              />
              {renderError("lastName")}
            </div>
            <div>
              <input
                name="dateOfBirth"
                value={patientForm.dateOfBirth}
                onChange={handleChange}
                className="w-full border p-2"
                placeholder="Date of Birth (YYYY-MM-DD)"
                type="date"
                required
                autoComplete="bday"
              />
              {renderError("dateOfBirth")}
            </div>
            <div>
              <input
                name="email"
                value={patientForm.email}
                onChange={handleChange}
                className="w-full border p-2"
                placeholder="Email"
                type="email"
                required
                autoComplete="email"
              />
              {renderError("email")}
            </div>
            <div>
              <input
                name="phoneNumber"
                value={patientForm.phoneNumber}
                onChange={handleChange}
                className="w-full border p-2"
                placeholder="(555) 555-1234"
                type="tel"
                inputMode="tel"
                maxLength={14}
                required
                autoComplete="tel"
              />
              {renderError("phoneNumber")}
            </div>
          </div>
        </div>
      </div>

      {/* Address */}
      <div className={sectionStyle(sections.address)}>
        <button
          onClick={() => toggleSection("address")}
          className="w-full flex items-center justify-between p-4 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded-t-lg"
        >
          <div className="flex items-center space-x-2">
            <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
              2
            </span>
            <Home className="w-5 h-5" />
            <span className="font-medium">Address</span>
          </div>
          {sections.address ? <ChevronUp /> : <ChevronDown />}
        </button>
        <div
          className={`transition-all duration-300 ease-in-out ${
            sections.address
              ? "max-h-screen opacity-100 overflow-visible" // <- allow the popup to overflow
              : "max-h-0 opacity-0 overflow-hidden"
          }`}
        >
          <div className="p-4 space-y-3 relative">
            {" "}
            {/* <- ensure a stacking context */}
            {/* Single visible field (autocomplete). City/State/Country are filled in state only. */}
            <div>
              <label className="block text-sm text-gray-700 mb-1">
                Address
              </label>
              <AddressAutocomplete
                value={patientForm.addressLine}
                placeholder="Start typing address…"
                onSelect={(addr) => {
                  setPatientForm((prev) => ({
                    ...prev,
                    addressLine: truncateStreet(
                      addr.addressLine ||
                        addr.line1 ||
                        addr.streetAddress ||
                        "",
                    ),
                    city: addr.city || prev.city,
                    state: addr.state || prev.state,
                    country: addr.country || prev.country,
                  }));
                }}
              />
            </div>
            {/* Keep a hidden input to participate in native validation if desired */}
            <input
              name="addressLine"
              value={patientForm.addressLine}
              onChange={handleChange}
              className="hidden"
              placeholder="Address Line"
              required
              readOnly
              autoComplete="street-address"
            />
            {renderError("addressLine")}
            {/* City/State/Country are NOT displayed, but stored for BE */}
            {/* <input name="city" className="hidden" value={patientForm.city} readOnly /> */}
            {/* <input name="state" className="hidden" value={patientForm.state} readOnly /> */}
            {/* <input name="country" className="hidden" value={patientForm.country} readOnly /> */}
          </div>
        </div>
      </div>

      {/* Insurance */}
      <div className={sectionStyle(sections.insurance)}>
        <button
          onClick={() => toggleSection("insurance")}
          className="w-full flex items-center justify-between p-4 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded-t-lg"
        >
          <div className="flex items-center space-x-2">
            <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
              4
            </span>
            <FileText className="w-5 h-5" />
            <span className="font-medium">Insurance</span>
          </div>
          {sections.insurance ? <ChevronUp /> : <ChevronDown />}
        </button>

        <div
          className={`transition-all duration-300 ease-in-out overflow-hidden ${
            sections.insurance
              ? "max-h-screen opacity-100"
              : "max-h-0 opacity-0"
          }`}
        >
          <div className="p-4 space-y-3">
            <div>
              <label className="block text-sm text-gray-700 mb-1">
                Insurance Plan
              </label>
              <InsuranceDropdown
                value={patientForm.planId}
                onChange={(plan) =>
                  setPatientForm((prev) => ({
                    ...prev,
                    planId: plan?.planId || plan?.id || plan?.planName || "",
                  }))
                }
              />
              {renderError("planId")}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <input
                  name="memberId"
                  value={patientForm.memberId}
                  onChange={handleChange}
                  className="w-full border p-2"
                  placeholder="Member ID"
                />
                {renderError("memberId")}
              </div>

              <div>
                <input
                  name="groupId"
                  value={patientForm.groupId}
                  onChange={handleChange}
                  className="w-full border p-2"
                  placeholder="Group ID"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  Coverage Start
                </label>
                <input
                  name="coverageStart"
                  type="date"
                  value={patientForm.coverageStart}
                  onChange={handleChange}
                  className="w-full border p-2"
                />
                {renderError("coverageStart")}
              </div>

              <div>
                <label className="block text-sm text-gray-700 mb-1">
                  Coverage End
                </label>
                <input
                  name="coverageEnd"
                  type="date"
                  value={patientForm.coverageEnd}
                  onChange={handleChange}
                  className="w-full border p-2"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Document */}
      <div className={sectionStyle(sections.document)}>
        <button
          onClick={() => toggleSection("document")}
          className="w-full flex items-center justify-between p-4 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded-t-lg"
        >
          <div className="flex items-center space-x-2">
            <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
              3
            </span>
            <FileText className="w-5 h-5" />
            <span className="font-medium">Document</span>
          </div>
          {sections.document ? <ChevronUp /> : <ChevronDown />}
        </button>
        <div
          className={`transition-all duration-300 ease-in-out overflow-hidden ${
            sections.document ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="p-4 space-y-3">
            <select
              name="documentType"
              value={patientForm.documentType}
              onChange={handleChange}
              className="w-full border p-2"
            >
              <option value="">Select Document Type</option>
              <option value="INSURANCE">Insurance</option>
              <option value="ID">ID Card</option>
              <option value="LAB_RESULT">Lab Result</option>
            </select>
            <select
              name="documentCategory"
              value={patientForm.documentCategory}
              onChange={handleChange}
              className="w-full border p-2"
            >
              <option value="">Select Document Category</option>
              <option value="PRIMARY">Primary</option>
              <option value="SECONDARY">Secondary</option>
            </select>
            <input
              name="documentFile"
              type="file"
              onChange={handleFileChange}
              className="w-full"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-center">
        <button
          onClick={handleSubmit}
          className="mt-6 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
        >
          Create Account
        </button>
      </div>
    </div>
  );
};

export default PatientCreateAccount;
